import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { MediaImage } from "@app/scripts/react-general-components/MediaImage";
import { useShopifyCart, useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { ProductSizeSelect } from "@app/scripts/react-product-details/components/ProductSizeSelect";
import { ProductGwpCartSelect } from "@app/scripts/react-cart/components/ProductGwpCartSelect";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_VARIANT_STOCK_LEVEL } from "@app/scripts/utils/queries";
import { CartQty } from "@app/scripts/react-cart/components/CartQty";
import { CartProductStockStatus } from "@app/scripts/react-cart/components/CartProductStockStatus";
import { CartNotifyForm } from "@app/scripts/react-cart/components/CartNotifyForm";
import { appendSSSizeOptionToUrl, decodeShopifyId, sortVariants } from "@app/scripts/utils/index";

import type { Cart, LineItem, NormalisedProduct } from "@app/scripts/types";
import config from "@app/config";
import { ProductPrice } from "@app/scripts/react-product/components/ProductPrice";

type CartProductProps = {
  lineItem: LineItem;
  products: NormalisedProduct[];
  handleUpdate: ReturnType<typeof useShopifyCart>["handleUpdate"];
  handleAdd: ReturnType<typeof useShopifyCart>["handleAdd"];
  cart: Cart;
  addOosVariant: (variantId: number) => void;
  giftIds?: Array<string | number>;
  giftMessage?: string | null;
  lowStockThreshold: number;
  notifyLabel: string;
  setError: ReturnType<typeof useShopifyCart>["setError"];
  gifts?: NormalisedProduct[];
  showProperties?: boolean;
};

export const CartProduct: React.FC<CartProductProps> = ({
  lineItem,
  products,
  handleUpdate,
  handleAdd,
  cart,
  addOosVariant,
  giftMessage,
  lowStockThreshold,
  notifyLabel,
  setError,
  giftIds,
  gifts,
  showProperties,
}) => {
  const { getColor, getBadges } = useShopifyProduct({});

  const [loading, setLoading] = useState(false);
  const [oos, setOos] = useState(false);
  const [maxQty, setMaxQty] = useState<number | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<number>(lineItem.variant_id);
  const [isNotifyFormShowing, setIsNotifyFormShowing] = useState<boolean>(false);
  const [giftColourVariants, setGiftColourVariants] = useState();
  const [giftSizeVariants, setGiftSizeVariants] = useState();

  const isKid = document.querySelector(".is-industrie-kids") ? true : false;
  const isCartTemplate = document.querySelector(".template-cart") ? true : false;
  const isGift = lineItem.properties?.[config?.attributes?.isGwp] === "true";
  const matchingProduct = useMemo(() => products.find((p) => p.id === lineItem.product_id), [products, lineItem]);
  const color = useMemo(() => getColor(matchingProduct), [matchingProduct, getColor]);
  const badges = useMemo(() => getBadges(matchingProduct), [matchingProduct, getBadges]);
  const title = lineItem?.title?.split(/-.*-/)[0];

  const sizeValue = useMemo(
    () => lineItem.options_with_values?.find((opt) => opt.name.toLowerCase() === "size")?.value,
    [lineItem]
  );

  const urlWithSSData = useMemo(() => {
    return appendSSSizeOptionToUrl(lineItem.url, sizeValue);
  }, [lineItem.url, sizeValue]);

  const giftCardPropertyKeys = ["From", "To", "Message", "Email", "Email Subject", "Delivery Date"];
  const giftCardProperties = Object.entries(lineItem.properties || {})
    .filter(([key]) => giftCardPropertyKeys.includes(key))
    .map(([name, value]) => ({
      name,
      value,
    }));

  const colorSanitised = color ? color[0]?.trim() : "";
  const selectedGiftKey = `selected_gift-${matchingProduct?.id}`;
  const [giftSize, setGiftSize] = useState<string | number | null>(lineItem.variant_id);

  // Size change function for normal products (Non GWP products)
  const onSizeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLoading(true);
    const newVariantId = parseInt(e.target.value);
    const itemInCart = cart?.items.find(
      (item) => item.variant_id === newVariantId && item.variant_id !== lineItem.variant_id
    );
    const qty = lineItem.quantity + (itemInCart?.quantity ?? 0);

    const updatedOos = await fetchVariantStock(newVariantId);
    setOos(updatedOos);

    if (!updatedOos) {
      if (isGift) {
        setGiftSize(newVariantId);
        sessionStorage.setItem(selectedGiftKey, "true");
      }
      await handleUpdate(lineItem.key, 0, false);

      // Preserve existing line item properties when changing variants
      const hasCustomProperties = lineItem.properties && Object.keys(lineItem.properties).length > 0;

      const preservedProperties = isGift
        ? {
            [config?.attributes?.isGwp]: "true",
          }
        : hasCustomProperties
        ? { ...lineItem.properties }
        : null;

      await handleAdd({
        variantId: newVariantId,
        qty,
        properties: preservedProperties,
      });

      setIsNotifyFormShowing(false);
    } else {
      setSelectedVariant(Number(newVariantId));
    }

    setLoading(false);
  };

  // Variant change function for GWP products (Colour and Size)
  const onGWPVariantChange = useCallback(
    async (Id: number, variantType: string, parentId: number) => {
      setLoading(true);

      if (!Id || !variantType) {
        setLoading(false);
        return;
      }
      let newVariantId = Id;

      if (isGift) {
        if (variantType === "size") {
          setGiftSize(newVariantId);
        }
        // clear All keys
        Object.keys(sessionStorage)?.forEach((key) => {
          if (key.includes("selected_gift-")) sessionStorage.removeItem(key);
        });

        sessionStorage.setItem(`selected_gift-${parentId}`, "true");
      }

      const currentVariantId = selectedVariant;

      await handleUpdate(lineItem.key, 0, false);

      const add = await handleAdd({
        variantId: newVariantId,
        qty: 1,
        properties: {
          [config?.attributes?.isGwp]: "true",
          _sizeTag: matchingProduct?.tags.find((tag) => tag.includes("size--")) || "",
        },
      });

      // if there is an error adding the new variant, reselect the old one
      if (add?.error) {
        await handleAdd({
          variantId: currentVariantId,
          qty: 1,
          properties: {
            [config?.attributes?.isGwp]: "true",
            _sizeTag: matchingProduct?.tags.find((tag) => tag.includes("size--")) || "",
          },
        });
        setError(add.error);
        setTimeout(() => setError(null), 6000);
      }

      setLoading(false);
    },
    [selectedVariant]
  );

  useEffect(() => {
    if (giftIds?.length === 1 && isGift && !giftSize && matchingProduct?.variants?.length > 1)
      setError("Please select a size for your gift.");
  }, [isGift, giftSize, matchingProduct?.variants]);

  const onQtyChange = useCallback(
    async (value: number) => {
      setLoading(true);
      const result = await handleUpdate(lineItem.key, value);
      setLoading(false);
      return result;
    },
    [setLoading, handleUpdate, lineItem.variant_id]
  );

  const removeItem = useCallback(async () => {
    // if manually removed, store this in local storage to prevent auto adding back
    if (isGift && giftIds?.length === 1) {
      sessionStorage.setItem(`gift_removed:${lineItem.product_id}`, "true");
    }
    onQtyChange(0);
  }, [onQtyChange, lineItem.product_id, isGift, giftIds]);

  const fetchVariantStock = useCallback(async (variantId: number) => {
    const data = await shopify(GET_VARIANT_STOCK_LEVEL, {
      nodeId: btoa("gid://shopify/ProductVariant/" + variantId),
    });

    setMaxQty(data?.node?.quantityAvailable || null);

    if (data?.node?.availableForSale === false && data?.node?.currentlyNotInStock === false) {
      addOosVariant(lineItem.variant_id);
      return true;
    } else {
      return false;
    }
  }, []);

  useEffect(() => {
    fetchVariantStock(lineItem.variant_id);
  }, []);

  const getParent = useCallback(
    (product: NormalisedProduct) => {
      const parentHandle = product?.tags?.find((tag) => tag.includes("parent--"))?.replace("parent--", "");
      return product?.tags.includes("parent") ? product : gifts?.find((gift) => gift.handle === parentHandle);
    },
    [gifts]
  );

  // Checks if GWP product has any other sizes
  useEffect(() => {
    const fetchGiftSizes = async () => {
      if (!matchingProduct) return;

      const parentProduct = getParent(matchingProduct);
      const relatedHandles = parentProduct?.tags
        ?.filter((tag) => tag.includes("size--"))
        ?.map((t) => t.replace("size--", ""));
      const relatedProducts = gifts?.filter((gift) => relatedHandles.includes(gift.handle));

      const allSizes = sortVariants(
        [parentProduct, ...relatedProducts].flatMap((product) =>
          product?.variants?.map((variant) => ({
            ...variant,
            id: Number(decodeShopifyId(variant?.id, "ProductVariant")),
            parentId: Number(decodeShopifyId(product?.id, "Product")),
          }))
        )
      );

      setGiftSizeVariants(allSizes || []);
    };

    if (isGift) {
      fetchGiftSizes();
    }
  }, [matchingProduct, gifts]);

  // Used to check related colours variants for gift product
  useEffect(() => {
    const colourVariantHandler = async () => {
      if (!matchingProduct) return;
      const parentProduct = getParent(matchingProduct);

      // "style--" is a legacy tag for backwards compatibility purposes, can be removed post 08/2025
      const styleTag = parentProduct?.tags.find((tag) => tag.includes("altCode--") || tag.includes("style--"));
      if (!styleTag) return;

      const related = gifts?.filter((p) => p.tags?.includes(styleTag));
      if (!related) return;

      setGiftColourVariants(
        related?.map((c: any) => ({
          ...c,
          colour: c.title?.split("-")[1]?.trim(),
          variant_id: decodeShopifyId(c?.id, "Product"),
        }))
      );
    };

    if (isGift) {
      colourVariantHandler();
    }
  }, [gifts]);

  return (
    <div className="cart__line-item-wrapper">
      <div
        className={`cart__line-item ${oos ? "is-oos" : ""} ${isGift ? "is-gift" : ""}`}
        style={{ opacity: loading ? 0.6 : undefined, pointerEvents: loading ? "none" : undefined }}
      >
        {matchingProduct?.media?.[0] ? (
          <a href={urlWithSSData} title={title} className="block">
            <MediaImage media={matchingProduct?.media?.[0]} />
          </a>
        ) : (
          <div className="cart__line-item__placeholder" />
        )}

        <div className="cart__line-item__content">
          <div className="cart__line-item__content-details">
            <div>
              <h3 className="mb-2">
                <a href={urlWithSSData} title={title} className="cart__line-item__title text-md bold">
                  {title}
                </a>
              </h3>
              {!isGift && badges && badges.length > 0 && (
                <div className="cart__line-item__badges">
                  {badges?.map((badge) => (
                    <span className="badge" data-flagcolor={badge.tag}>
                      {badge.title.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
              )}
              {color && <div className="text-sm text-secondary-grey caps">{color}</div>}

              {giftCardProperties && showProperties && (
                <div className="mt-2">
                  {giftCardProperties.map((property) => (
                    <div key={property.name} className="text-sm text-secondary-grey">
                      <span>{property.name}: </span>
                      <span>{property.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isGift ? (
              <ProductPrice product={matchingProduct} lineItem={lineItem} showCartDiscount />
            ) : (
              <span className="text-md bold">Free</span>
            )}
          </div>

          {isGift && giftMessage && <div className="cart__line-item__gift-message text-sm">{giftMessage}</div>}

          <div className="cart__line-item__options">
            {giftColourVariants && isGift && isKid && (
              <ProductGwpCartSelect
                products={giftColourVariants}
                selectedVariant={colorSanitised}
                handleChangeVariant={onGWPVariantChange}
                variantType="colour"
              />
            )}

            {giftSizeVariants && isGift && isKid && (
              <ProductGwpCartSelect
                products={giftSizeVariants}
                selectedVariant={giftSize}
                handleChangeVariant={onGWPVariantChange}
                variantType="size"
              />
            )}

            {matchingProduct && !isGift && (
              <ProductSizeSelect
                variants={matchingProduct.variants}
                selectedId={lineItem.variant_id}
                handleChangeVariant={onSizeChange}
              />
            )}

            {!isGift && <CartQty lineItem={lineItem} onQtyChange={onQtyChange} oos={oos} isKid={isKid} />}

            {oos && isKid && isCartTemplate && (
              <Button
                variant="small"
                color="grey"
                label="Notify Me When Available"
                className="hidden md:inline-block mt-0 cart__line-item__notify"
                onClick={() => setIsNotifyFormShowing(true)}
              />
            )}
          </div>

          <div className="cart__line-item__bottom-wrap">
            {oos && isKid && isCartTemplate && (
              <Button
                variant="small"
                color="grey"
                label="Notify Me When Available"
                className="block md:hidden mt-0 cart__line-item__notify"
                onClick={() => setIsNotifyFormShowing(true)}
              />
            )}

            {oos && isKid && !isCartTemplate && !isNotifyFormShowing && (
              <Button
                variant="small"
                color="grey"
                label="Notify Me When Available"
                className="block mt-1 cart__line-item__notify"
                onClick={() => setIsNotifyFormShowing(true)}
              />
            )}

            {(oos || maxQty === lowStockThreshold) && (
              <CartProductStockStatus status={oos ? "out-of-stock" : "low-stock"} />
            )}

            {oos && !isKid && (
              <Button
                variant="small"
                color="grey"
                label="Notify Me"
                className="mt-0 cart__line-item__notify"
                onClick={() => setIsNotifyFormShowing(true)}
              />
            )}

            <div className="cart__line-item__bottom">
              {/* data-variant not used in solution. Requested by analyzify */}
              <Button
                variant="text"
                onClick={removeItem}
                label={"Remove"}
                ariaLabel={"Remove"}
                data-variant={lineItem.variant_id}
              />
            </div>
          </div>
        </div>
      </div>

      {isNotifyFormShowing && (
        <CartNotifyForm variantId={selectedVariant} productId={lineItem.product_id} notifyLabel={notifyLabel} />
      )}
    </div>
  );
};
